import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, RefreshCw, KeyRound, Copy, Check } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Security2FAPanel = ({ authFetch }) => {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/2fa/status`);
      const j = await r.json();
      setEnabled(!!j.enabled);
    } catch (e) { /* noop */ }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const startSetup = async () => {
    try {
      setBusy(true); setBackup([]);
      const r = await authFetch(`${API}/2fa/setup`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error');
      setSetup(j);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const enable = async () => {
    if (code.trim().length < 6) { toast.error('Ingresa el código de 6 dígitos'); return; }
    try {
      setBusy(true);
      const r = await authFetch(`${API}/2fa/enable`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: code.trim() }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error');
      toast.success('2FA activado');
      setBackup(j.backup_codes || []); setSetup(null); setCode(''); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const disable = async () => {
    const c = window.prompt('Para desactivar el 2FA ingresa un código actual o de respaldo:');
    if (!c) return;
    try {
      setBusy(true);
      const r = await authFetch(`${API}/2fa/disable`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: c.trim() }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error');
      toast.success('2FA desactivado'); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <section className="space-y-6" data-testid="security-2fa-panel">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">FASE 1 · Seguridad</p>
        <h3 className="mt-1 text-lg font-bold text-text-heading">Doble factor (2FA) del administrador</h3>
        <p className="mt-1 text-xs text-text-muted">Protege tu acceso con una app autenticadora. El bloqueo por intentos fallidos ya está activo en el login.</p>
      </div>

      <div className="glass-card p-5" data-testid="twofa-status-card">
        <div className="flex items-center gap-3">
          {enabled ? <ShieldCheck className="h-8 w-8 text-emerald-400" /> : <ShieldAlert className="h-8 w-8 text-amber-400" />}
          <div>
            <p className="text-sm font-semibold text-text-heading">{enabled === null ? 'Comprobando…' : enabled ? '2FA activo' : '2FA no activo'}</p>
            <p className="text-xs text-text-muted">{enabled ? 'Tu cuenta pide un código al iniciar sesión.' : 'Recomendado para cuentas con acceso total.'}</p>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          {!enabled && <button type="button" onClick={startSetup} disabled={busy} data-testid="twofa-setup-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-semibold text-background disabled:opacity-50"><KeyRound className="h-4 w-4" /> Configurar 2FA</button>}
          {enabled && <button type="button" onClick={disable} disabled={busy} data-testid="twofa-disable-btn" className="inline-flex items-center gap-2 rounded-full border border-rose-400/30 px-5 py-2.5 text-xs font-semibold text-rose-200">Desactivar 2FA</button>}
        </div>
      </div>

      {setup && (
        <div className="glass-card p-5" data-testid="twofa-setup-card">
          <h4 className="mb-3 text-sm font-semibold text-text-heading">1. Escanea el código con tu app (Google Authenticator, Authy…)</h4>
          <div className="flex flex-wrap items-center gap-6">
            <img src={setup.qr_data_url} alt="QR 2FA" className="h-44 w-44 rounded-xl bg-white p-2" data-testid="twofa-qr" />
            <div className="text-xs text-text-muted">
              <p className="mb-2">¿No puedes escanear? Ingresa esta clave manualmente:</p>
              <code className="rounded-lg border border-border-subtle bg-background/70 px-3 py-2 text-text-heading" data-testid="twofa-secret">{setup.secret}</code>
            </div>
          </div>
          <h4 className="mb-2 mt-5 text-sm font-semibold text-text-heading">2. Ingresa el código de 6 dígitos para activar</h4>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="twofa-code-input"
              className="rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading focus:border-primary focus:outline-none" />
            <button type="button" onClick={enable} disabled={busy} data-testid="twofa-enable-btn" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-xs font-semibold text-background disabled:opacity-50"><Check className="h-4 w-4" /> Activar</button>
          </div>
        </div>
      )}

      {backup.length > 0 && (
        <div className="glass-card p-5" data-testid="twofa-backup-card">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-text-heading"><Copy className="h-4 w-4 text-primary" /> Guarda tus códigos de respaldo</h4>
          <p className="mb-3 text-xs text-text-muted">Úsalos si pierdes tu teléfono. Cada uno funciona una sola vez.</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="twofa-backup-codes">
            {backup.map((b) => <code key={b} className="rounded-lg border border-border-subtle bg-background/70 px-3 py-2 text-center text-sm text-text-heading">{b}</code>)}
          </div>
        </div>
      )}
    </section>
  );
};

export default Security2FAPanel;
